import { PageSchemaSchema, type Block } from './blocks';
import type { Market } from '../dataProvider';

/**
 * The two-layer publish-time validator (D-page-schema-editor-8b), the GOING-LIVE
 * GATE. It runs IN THE SURFACE, BEFORE the chokepoint write; publish proceeds only
 * if both layers pass (the chokepoint guards the capability, it does not validate
 * content). Save (8a) is forgiving (per-block, no whole-schema parse, no
 * resolvability); publish is strict and resolvable.
 *
 * LAYER 1 (the reused 8a mirror, whole-schema scope): construct the CANDIDATE
 * published schema (the draft plus the next version int and a now ISO publishedAt)
 * and parse it with PageSchemaSchema, the SAME mirror the 8a chip / save use per
 * block. NOT a second validation path.
 *
 * LAYER 2 (catalog-membership resolvability, the one place Zod is not enough): a
 * slug that is a non-empty string passes Layer 1 but may not point at a LIVE catalog
 * entry (a deleted or renamed brand or category), which renders a broken block in
 * the apps. So assert every referenced slug is a member of the live catalog. The
 * 8a resolveStore / resolveCategory fall back to the raw slug on a miss, so they
 * CANNOT signal "unknown"; this uses an explicit membership Set.
 */

/** A block that references a slug not present in the live catalog. */
export interface RefProblem {
  blockType: Block['type'];
  blockId: string;
  kind: 'store' | 'category';
  slug: string;
}

/** Every store / category slug a block references (for the membership check). */
const blockRefs = (
  block: Block,
): { kind: 'store' | 'category'; slug: string }[] => {
  switch (block.type) {
    case 'storeGrid':
      return block.data.storeIds.map((slug) => ({ kind: 'store', slug }));
    case 'featuredStore':
      return [{ kind: 'store', slug: block.data.storeId }];
    case 'carousel':
      return [
        ...(block.data.storeIds ?? []).map(
          (slug) => ({ kind: 'store', slug }) as const,
        ),
        ...(block.data.categoryIds ?? []).map(
          (slug) => ({ kind: 'category', slug }) as const,
        ),
      ];
    case 'categoryStrip':
      return block.data.categoryIds.map((slug) => ({ kind: 'category', slug }));
    case 'banner':
    case 'pillCta':
      // A link to a store / category is a catalog reference; page / external are
      // free strings (a page id or a url) and out of scope for resolution.
      return block.data.link.kind === 'store' ||
        block.data.link.kind === 'category'
        ? [{ kind: block.data.link.kind, slug: block.data.link.ref }]
        : [];
    default:
      return [];
  }
};

/**
 * LAYER 2: the blocks that reference a slug missing from the live catalog. Empty
 * means every reference resolves.
 */
export const referenceProblems = (
  blocks: Block[],
  storeSlugs: Set<string>,
  categorySlugs: Set<string>,
): RefProblem[] => {
  const problems: RefProblem[] = [];
  for (const block of blocks) {
    for (const ref of blockRefs(block)) {
      const set = ref.kind === 'store' ? storeSlugs : categorySlugs;
      if (!set.has(ref.slug)) {
        problems.push({
          blockType: block.type,
          blockId: block.id,
          kind: ref.kind,
          slug: ref.slug,
        });
      }
    }
  }
  return problems;
};

/** A human message for a dangling reference (names the offending block and slug). */
export const formatRefProblem = (p: RefProblem): string =>
  `The ${p.blockType} block ${p.blockId} references a missing ${p.kind}: ${p.slug}`;

/**
 * LAYER 1: parse the candidate published schema with the reused mirror. Returns the
 * first issue message, or null when the whole schema is valid.
 */
export const schemaProblem = (
  pageId: string,
  market: Market,
  version: number,
  publishedAt: string,
  blocks: Block[],
): string | null => {
  const candidate = { pageId, market, version, publishedAt, blocks };
  const result = PageSchemaSchema.safeParse(candidate);
  if (result.success) return null;
  const issue = result.error.issues[0];
  const path = issue.path.join('.');
  return path ? `${path}: ${issue.message}` : issue.message;
};

/**
 * Run both layers over a set of blocks being published. `version` / `publishedAt`
 * are the candidate's (Layer 1 needs a positive version and an ISO publishedAt, the
 * publish-time values). `runSchema` is false for a rollback (a prior snapshot was
 * already shape-valid when first published, so only resolvability is re-checked).
 */
export const validateForPublish = (params: {
  pageId: string;
  market: Market;
  version: number;
  publishedAt: string;
  blocks: Block[];
  storeSlugs: Set<string>;
  categorySlugs: Set<string>;
  runSchema?: boolean;
}): string[] => {
  const errors: string[] = [];
  if (params.runSchema !== false) {
    const sp = schemaProblem(
      params.pageId,
      params.market,
      params.version,
      params.publishedAt,
      params.blocks,
    );
    if (sp) errors.push(sp);
  }
  for (const p of referenceProblems(
    params.blocks,
    params.storeSlugs,
    params.categorySlugs,
  )) {
    errors.push(formatRefProblem(p));
  }
  return errors;
};
